import type { RefObject } from 'react'

import {
    gsap,
    ms,
    registerGsap,
    useGSAP,
    wfEase
} from '@/lib/gsap'

/**
 * Page-load intro, recreated from the Webflow PAGE_START / PAGE_FINISH
 * interactions (IX2 lists a-6, a-64 for the homepage split hero and a-59 for
 * the standard inner pages). Initial states are set before paint so there is
 * no flash, then elements animate in on their original stagger.
 *
 * The homepage variant is auto-detected by the presence of `.hero-text-sticky`.
 */
export const usePageLoad = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(
        () => {
            registerGsap()

            const root = scope.current
            if (!root) return

            const isHome = Boolean(root.querySelector('.hero-text-sticky'))
            const halfViewport = (): number => -0.5 * window.innerHeight
            const ease = wfEase('ease')
            const tl = gsap.timeline()

            if (isHome) {
                gsap.set('.hero-text-sticky', { y: halfViewport, scale: 0.8 })
                gsap.set('.main-nav', { opacity: 0 })
                gsap.set('.ellipses-holder', { opacity: 0 })
                gsap.set('.hero-product-demo', { opacity: 0 })
                gsap.set('.hero-text._1', { xPercent: 100 })
                gsap.set('.hero-text._2', { xPercent: -100 })
                gsap.set('.main-heading', { y: 20, skewX: -3, skewY: -3, opacity: 0 })
                gsap.set('.hero-description', { y: 20, opacity: 0 })
                gsap.set('.download-buttons.hero-download-buttons', { y: 20, opacity: 0 })
                gsap.set('.hero-top-content', { yPercent: 100 })

                tl.to('.hero-text._1', { xPercent: 0, duration: ms(1000), ease }, 0)
                tl.to('.hero-text._2', { xPercent: 0, duration: ms(1000), ease }, 0)
                tl.to('.hero-text-sticky', { scale: 1, duration: ms(600), ease }, ms(900))
                tl.to('.main-nav', { opacity: 1, duration: ms(500), ease }, ms(1400))
                tl.to('.hero-text-sticky', { y: 0, duration: ms(800), ease }, ms(1600))
                tl.to('.ellipses-holder', { opacity: 1, duration: ms(500), ease }, ms(1600))
                tl.to('.download-buttons.hero-download-buttons', { y: 0, opacity: 1, duration: ms(500), ease }, ms(1900))
                tl.to('.main-heading', { opacity: 1, y: 0, skewX: 0, skewY: 0, duration: ms(500), ease }, ms(2000))
                tl.to('.hero-description', { y: 0, opacity: 1, duration: ms(500), ease }, ms(2200))
                tl.to('.hero-top-content', { yPercent: 0, duration: ms(500), ease }, ms(2200))
                tl.to('.hero-product-demo', { opacity: 1, duration: ms(500), ease }, ms(2400))

                return
            }

            gsap.set('.main-nav', { opacity: 0 })
            gsap.set('.main-heading', { opacity: 0, y: 20, skewX: -3, skewY: -3 })
            gsap.set('.ellipses-holder', { opacity: 0 })
            gsap.set('.hero-top-content', { yPercent: 100, opacity: 0 })
            gsap.set('.hero-description', { opacity: 0, y: 20 })
            gsap.set('.download-buttons.hero-download-buttons', { opacity: 0, y: 20 })
            gsap.set('.on-load-content', { opacity: 0 })
            gsap.set('.animation-on-page-load', { opacity: 0, y: 20 })

            tl.to('.main-nav', { opacity: 1, duration: ms(500), ease }, ms(400))
            tl.to('.main-heading', { opacity: 1, y: 0, skewX: 0, skewY: 0, duration: ms(500), ease }, ms(700))
            tl.to('.ellipses-holder', { opacity: 1, duration: ms(500), ease }, ms(700))
            tl.to('.hero-top-content', { yPercent: 0, opacity: 1, duration: ms(700), ease }, ms(700))
            tl.to('.hero-description', { opacity: 1, y: 0, duration: ms(500), ease }, ms(900))
            tl.to('.on-load-content', { opacity: 1, duration: ms(500), ease }, ms(900))
            tl.to('.download-buttons.hero-download-buttons', { opacity: 1, y: 0, duration: ms(700), ease }, ms(1100))
            tl.to('.animation-on-page-load', { opacity: 1, y: 0, duration: ms(700), ease }, ms(1300))
        },
        { scope }
    )
}

